import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

// Persistent, anonymous per-browser id (no PII) so we can count unique visitors.
export function getVisitorId() {
  try {
    let v = localStorage.getItem("sms_vid");
    if (!v) {
      v = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem("sms_vid", v);
    }
    return v;
  } catch {
    return "anon";
  }
}

const RETAILER_RE = /(amazon\.|amzn\.to|walmart\.|target\.|homedepot\.|lowes\.|etsy\.|ebay\.)/i;
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export default function TrafficTracker() {
  const location = useLocation();
  const lastPath = useRef(null);

  // Pageview on every route change (deduped per path).
  useEffect(() => {
    const path = location.pathname || "/";
    if (lastPath.current === path) return;
    lastPath.current = path;
    api.post("/track", {
      visitor_id: getVisitorId(),
      path,
      referrer: document.referrer || "",
      kind: "view",
    }).catch(() => {});
  }, [location.pathname]);

  // Outbound affiliate-link clicks (interest signal — Amazon owns actual earnings).
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target?.closest?.("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (!RETAILER_RE.test(href)) return;
      const path = location.pathname || "/";
      // Which video? Prefer a data-manual-id on/near the link, else a UUID in the path.
      const carrier = a.closest?.("[data-manual-id]");
      const manualId = carrier?.getAttribute?.("data-manual-id") || (path.match(UUID_RE)?.[0] ?? null);
      // Which product? A data-item-name, else the link's own text.
      const itemName = (a.getAttribute("data-item-name") || a.textContent || "").trim().slice(0, 200);
      api.post("/track", {
        visitor_id: getVisitorId(),
        path,
        referrer: "",
        kind: "affiliate_click",
        manual_id: manualId,
        item_url: href.slice(0, 700),
        item_name: itemName,
      }).catch(() => {});
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [location.pathname]);

  return null;
}
